import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
} from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { Destination } from '@prlw/core/destinations/destination.entity';
import { DestinationsProvider } from '@prlw/core/destinations/destinations.provider';
import { AllInstruments } from '@prlw/core/monitor/all-instruments.entity';
import { AllTableModifiersService } from '@prlw/core/monitor/all-table-modifiers.service';
import { AllTableColumn } from '@prlw/core/monitor/types/all-table';
import { Modifier } from '@prlw/core/monitor/types/common';
import {
  OverflowFader,
  OverflowState,
  ScrollableElements,
} from '@prlw/libs/overflow-fader/overflow-fader.class';
import { OverflowFaderFactory } from '@prlw/libs/overflow-fader/overflow-fader-factory.service';
import { AllTableService } from './all-table.service';
import { AllTableInstrumentsService } from '@prlw/core/monitor/all-table-instruments.service';
import { PagePreloaderService } from '@prlw/common/page/page-preloader.service';

@Component({
  selector: 'prlw-all-table',
  templateUrl: './all-table.container.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AllTableContainer implements OnDestroy {
  public data$: Observable<AllInstruments[] | null>;
  public columns$: Observable<AllTableColumn[]>;
  public modifiers$: Observable<Modifier | null>;
  public destination$: Observable<Destination | null>;
  public overflows$: Observable<OverflowState>;
  private readonly instrumentsSubscription?: Subscription;
  private readonly overflowFader: OverflowFader;
  public readonly allInstrumentsIsPending$: Observable<boolean>;
  public readonly availableFeature$: Observable<boolean | undefined>;

  constructor(
    destinationsProvider: DestinationsProvider,
    private readonly allTableService: AllTableService,
    allTableModifiersService: AllTableModifiersService,
    overflowFaderFactory: OverflowFaderFactory,
    cdr: ChangeDetectorRef,
    private readonly allTableInstrumentsService: AllTableInstrumentsService,
    public readonly pagePreloaderService: PagePreloaderService,
  ) {
    this.allInstrumentsIsPending$ =
      allTableInstrumentsService.allInstruments$.pipe(
        map(
          (allInstrumentsWithPending) => !!allInstrumentsWithPending?.pending,
        ),
      );
    this.data$ = allTableService.sortedAllInstruments$;
    this.availableFeature$ = allTableInstrumentsService.allInstruments$.pipe(
      map(
        (allInstrumentsWithPending) =>
          !(
            allInstrumentsWithPending?.error &&
            allInstrumentsWithPending.error instanceof HttpErrorResponse &&
            allInstrumentsWithPending.error.status === HttpStatusCode.Forbidden
          ),
      ),
    );
    this.columns$ = allTableService.allTableColumns$;
    this.modifiers$ = allTableModifiersService.modifier$;
    this.destination$ = destinationsProvider.currentDestination$;
    this.overflowFader = overflowFaderFactory.create(cdr);
    this.overflows$ = this.overflowFader.overflows$;
  }

  public ngOnDestroy(): void {
    this.instrumentsSubscription?.unsubscribe();
    this.overflowFader.destroy();
  }

  public sortColumn(columnName: string): void {
    this.allTableService.allTableSortColumn(columnName);
  }

  public updateColumn(columns: AllTableColumn[]): void {
    this.allTableService.updateColumn(columns);
  }

  public setScrollHorizontalPosition(scrollLeft: number): void {
    this.overflowFader.setScrollHorizontalPosition(scrollLeft);
  }

  public setScrollVerticalPosition(scrollTop: number): void {
    this.overflowFader.setScrollVerticalPosition(scrollTop);
  }

  public initResizeObserver(elems: ScrollableElements): void {
    this.overflowFader.init(elems);
  }
}
